import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardSummary } from '../api';
import { DonutChart, DonutLegend, BarRow } from './charts';
import { usePermissions } from '../utils/permissions';
import {
  FiUsers, FiMapPin, FiBookOpen, FiLayers, FiHome, FiClipboard, FiActivity,
  FiUserCheck, FiDollarSign, FiPlus, FiUpload, FiFileText,
} from 'react-icons/fi';

const ACTION_BADGE = {
  create: 'badge-success', update: 'badge-info', delete: 'badge-danger',
  login: 'badge-muted', approve: 'badge-success', reject: 'badge-danger',
  checkout: 'badge-gold', import: 'badge-info', export: 'badge-info',
};

function KpiCard({ icon: Icon, label, value, colorClass, to }) {
  const navigate = useNavigate();
  return (
    <div className={`stat-card ${colorClass}`} onClick={() => to && navigate(to)} style={{ cursor: to ? 'pointer' : 'default' }}>
      <div className="stat-icon"><Icon /></div>
      <div className="stat-content">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getDashboardSummary()
      .then(res => setData(res.data))
      .catch(() => setError('Failed to load dashboard data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }
  if (error || !data) {
    return <div className="alert alert-error">{error || 'No data available'}</div>;
  }

  const maxCampus = Math.max(1, ...data.students.byCampus.map(c => c.count));
  const maxCourse = Math.max(1, ...data.students.byCourse.map(c => c.count));

  const showStudents = can('students', 'view');
  const showCampuses = can('campuses', 'view');
  const showCourses = can('courses', 'view');
  const showHostels = can('hostels', 'view');
  const showResidents = can('hostel_admissions', 'view');
  const showRequests = can('hostel_requests', 'view');
  const showFinances = can('hostel_finances', 'view');
  const showAuditLog = can('audit_log', 'view');

  const topStats = [showStudents, showCampuses, showCourses].some(Boolean);
  const hostelStats = [showHostels, showResidents, showRequests, showFinances].some(Boolean);
  const canBulkImport = can('bulk_import_students', 'create') || can('bulk_import_hostel', 'create');
  const quickActions = [can('students', 'create'), canBulkImport, can('hostel_admissions', 'create'), can('hostel_invoices', 'create')].some(Boolean);

  return (
    <div>
      <div className="card">
        <h2 className="card-title">Dashboard</h2>
        <p style={{ color: 'var(--text-muted)', marginTop: '6px' }}>
          Welcome to the Hazza Institute of Technology Candidate &amp; Hostel Management System
        </p>
      </div>

      {/* Top KPIs */}
      {topStats && (
        <div className="stats-grid">
          {showStudents && <KpiCard icon={FiUsers} label="Total Students" value={data.students.total} colorClass="stat-purple" to="/students" />}
          {showCampuses && <KpiCard icon={FiMapPin} label="Campuses" value={data.campuses.total} colorClass="stat-blue" to="/campuses" />}
          {showCourses && <KpiCard icon={FiBookOpen} label="Courses" value={data.courses.total} colorClass="stat-green" to="/courses" />}
          {showCourses && <KpiCard icon={FiLayers} label="NVQF Levels" value={data.levels.total} colorClass="stat-orange" />}
        </div>
      )}

      {hostelStats && (
        <div className="stats-grid">
          {showHostels && <KpiCard icon={FiHome} label="Hostel Accounts" value={data.hostels.total} colorClass="stat-purple" to="/hostels" />}
          {showResidents && <KpiCard icon={FiUserCheck} label="Active Residents" value={data.hostels.activeResidents} colorClass="stat-green" to="/hostel-admissions" />}
          {showRequests && <KpiCard icon={FiClipboard} label="Pending Requests" value={data.requests.pending} colorClass="stat-orange" to="/hostel-requests" />}
          {showFinances && <KpiCard icon={FiDollarSign} label="Total Paid to Hostels" value={`PKR ${data.finance.totalPaid.toLocaleString()}`} colorClass="stat-blue" to="/hostel-finances" />}
        </div>
      )}

      {/* Quick actions */}
      {quickActions && (
        <div className="card">
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {can('students', 'create') && <button className="btn btn-primary btn-small" onClick={() => navigate('/students')}><FiPlus /> Add Student</button>}
            {canBulkImport && <button className="btn btn-secondary btn-small" onClick={() => navigate('/bulk-import')}><FiUpload /> Bulk Import</button>}
            {can('hostel_admissions', 'create') && <button className="btn btn-secondary btn-small" onClick={() => navigate('/hostel-admissions')}><FiUserCheck /> Add Resident</button>}
            {can('hostel_invoices', 'create') && <button className="btn btn-secondary btn-small" onClick={() => navigate('/invoices')}><FiFileText /> Generate Invoice</button>}
          </div>
        </div>
      )}

      {(showStudents || showResidents) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }} className="dashboard-charts-grid">
          {/* Students by campus */}
          {showStudents && (
            <div className="card">
              <h3 className="card-title" style={{ fontSize: '15px' }}><FiMapPin /> Students by Campus</h3>
              <div style={{ marginTop: '18px' }}>
                {data.students.byCampus.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No data yet.</p>
                ) : data.students.byCampus.map((c, i) => (
                  <BarRow key={i} label={c.label} value={c.count} max={maxCampus} color="var(--color-primary)" formatValue={v => v} />
                ))}
              </div>
            </div>
          )}

          {/* Hostel occupancy donut */}
          {showResidents && (
            <div className="card">
              <h3 className="card-title" style={{ fontSize: '15px' }}><FiHome /> Hostel Occupancy</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginTop: '14px', flexWrap: 'wrap' }}>
                <DonutChart segments={[
                  { label: 'Active', value: data.hostels.activeResidents, color: '#10b981' },
                  { label: 'Exited', value: data.hostels.exitedResidents, color: '#6366f1' },
                ]} />
                <DonutLegend segments={[
                  { label: 'Active residents', value: data.hostels.activeResidents, color: '#10b981' },
                  { label: 'Exited residents', value: data.hostels.exitedResidents, color: '#6366f1' },
                ]} />
              </div>
            </div>
          )}
        </div>
      )}

      {(showStudents || showAuditLog) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }} className="dashboard-charts-grid">
          {/* Top courses */}
          {showStudents && (
            <div className="card">
              <h3 className="card-title" style={{ fontSize: '15px' }}><FiBookOpen /> Top Courses by Enrollment</h3>
              <div style={{ marginTop: '18px' }}>
                {data.students.byCourse.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No data yet.</p>
                ) : data.students.byCourse.map((c, i) => (
                  <BarRow key={i} label={c.label} value={c.count} max={maxCourse} color="var(--color-gold-dark)" formatValue={v => v} />
                ))}
              </div>
            </div>
          )}

          {/* Recent activity */}
          {showAuditLog && (
            <div className="card">
              <div className="card-header" style={{ marginBottom: '10px' }}>
                <h3 className="card-title" style={{ fontSize: '15px' }}><FiActivity /> Recent Activity</h3>
                <button className="btn btn-secondary btn-small" onClick={() => navigate('/audit-log')}>View All</button>
              </div>
              <div style={{ display: 'grid', gap: '8px' }}>
                {data.recentActivity.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No recent activity.</p>
                ) : data.recentActivity.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: '#f7f8fc', borderRadius: '8px' }}>
                    <span className={`badge ${ACTION_BADGE[a.action] || 'badge-muted'}`} style={{ flexShrink: 0 }}>{a.action}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '12.5px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.record_label || a.module}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {a.actor_name || 'System'} · {new Date(a.created_at).toLocaleString('en-PK')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .dashboard-charts-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
